// What the maintainer's model picker may OFFER on one donated key (arc 101
// slice 11, plan doc 05). Pure — no fetch, no component — so the three shapes a
// ceiling can take are provable without rendering anything.
//
// Two inputs, and they are not the same kind of thing:
//
//   - `allowed_models` is the DONOR's ceiling, per gift. It is the authorization,
//     and the API enforces it (`PATCH /v1/donations/:id/model` → 422 naming what
//     is permitted); the pool resolver re-applies it at seal time. Nothing here
//     re-implements that — this only decides what a picker may show, so a
//     maintainer is never offered a model that would plainly be refused.
//   - `model_options` is the platform CATALOG, served on the received page
//     because the SPA cannot import `@developerz/config`. It is a vocabulary,
//     never an authorization, and it is spent only when the ceiling is `*`.
//
// The wildcard rule is the DOMAIN's (`modelPermitted`): `*` ANYWHERE in the list
// permits anything the endpoint serves, so `['*', 'gpt-5']` is an open ceiling
// rather than a two-entry list. Restating that as "the list has one entry and it
// is a star" would be a second copy of a rule that already exists.

import { MODEL_WILDCARD } from '@developerz/domain';
import type { ModelOption } from './donations';

/**
 * How to render the picker for one gift.
 *
 * `fixed` is deliberately not `list` with one entry: a one-option dropdown asks
 * a maintainer to choose where there is no choice, and reads as a control that
 * is broken. It renders as static text — the model this key runs, stated.
 */
export type ModelPickerMode =
  /** Not an inference gift, or its key is gone — there is nothing to pick. */
  | { readonly kind: 'none' }
  /** Exactly one authorized model: static text, not a dropdown. */
  | { readonly kind: 'fixed'; readonly model: string }
  /** A concrete allowlist: exactly these options, and no free text. */
  | { readonly kind: 'list'; readonly models: readonly string[] }
  /**
   * `['*']` — the catalog, PLUS free text for a `custom` OpenAI-compatible
   * endpoint whose models we cannot enumerate (probing the donor's key to fill a
   * dropdown would spend their money). A typed model is flagged unverified for
   * exactly that reason: the ceiling permits it, we just cannot confirm the
   * endpoint serves it.
   */
  | { readonly kind: 'open'; readonly models: readonly string[] };

/** Trim, drop blanks, drop duplicates — a ceiling is a set of model ids. */
function clean(models: readonly string[]): readonly string[] {
  return [...new Set(models.map((m) => m.trim()).filter((m) => m !== ''))];
}

/**
 * The picker's shape for one gift's ceiling.
 *
 * `allowed` is the gift's `allowed_models` as the API serves it: absent on a
 * money/compute gift and on an `llm` gift whose key is gone, and already
 * RESOLVED for the legacy `allowed_models: null` shape (the server sends
 * `[keyModel]`, so a legacy key reads as the single-model gift it always was).
 * An empty or all-blank list is `none` rather than an empty dropdown — a picker
 * with no options is a dead control.
 */
export function modelPickerMode(
  allowed: readonly string[] | undefined,
  catalog: readonly ModelOption[],
): ModelPickerMode {
  const ceiling = clean(allowed ?? []);
  if (ceiling.length === 0) return { kind: 'none' };
  if (ceiling.includes(MODEL_WILDCARD)) return { kind: 'open', models: catalogModels(catalog) };
  const only = ceiling[0];
  if (ceiling.length === 1 && only !== undefined) return { kind: 'fixed', model: only };
  return { kind: 'list', models: ceiling };
}

/**
 * The catalog's model ids, deduped, in the order the API sent them (sorted
 * provider-then-model server-side, so the picker's order is the wire's).
 * Two providers serving the same model id are one option: the gift's key
 * decides which endpoint answers, so the provider is not the maintainer's
 * choice to make here.
 */
function catalogModels(catalog: readonly ModelOption[]): readonly string[] {
  return clean(catalog.map((o) => o.model));
}

/** The one authorized model, when there is exactly one — the static branch. */
export function fixedModel(mode: ModelPickerMode): string | undefined {
  return mode.kind === 'fixed' ? mode.model : undefined;
}

/**
 * The options a dropdown may offer, and whether free text rides beside them.
 * Undefined for the two modes that render no control at all — so a component
 * narrows on the value rather than re-testing `kind`.
 */
export function choosableModels(
  mode: ModelPickerMode,
): { readonly models: readonly string[]; readonly freeText: boolean } | undefined {
  if (mode.kind !== 'list' && mode.kind !== 'open') return undefined;
  return { models: mode.models, freeText: mode.kind === 'open' };
}
